/**
 * One-time pdfjs-dist worker setup + shared getDocument parameters.
 *
 * pdf.js is dynamic-imported so it stays out of the initial bundle, and the
 * worker is configured exactly once (subsequent calls reuse the cached module;
 * the first call's worker config wins).
 *
 * Three consumption contexts, one API:
 * - **Host app / internal arches** (default): the worker URL is resolved
 *   relative to the bundled module via `import.meta.url` so the bundler can
 *   fingerprint and serve it.
 * - **Sandboxed plugin iframe** (opaque origin, strict CSP): pass
 *   `workerModuleUrl` — the platform-hosted pdf.js worker module URL (served
 *   from `cdn.{env}.fias.io`). The loader wraps it in a **`blob:` module
 *   worker** (see `createBlobModuleWorker`) that `import()`s the real worker.
 *   This is what the `sandbox:vendored-libraries` capability allows (pdf.js is a vendored library): `worker-src
 *   blob: <cdn>` to create the blob worker + `script-src <cdn>` for the import.
 *   (Empirically, a module worker's import graph is `worker-src`-governed, so
 *   BOTH the `blob:` and the CDN origin must be in `worker-src`; a bare
 *   `worker-src blob:` is NOT enough for a CDN-delivered worker.) If worker
 *   creation throws (e.g. CSP still blocks it), we swallow the error and leave
 *   `GlobalWorkerOptions` unset so pdf.js falls back to its main-thread "fake
 *   worker" — slower, but it still renders. (Wiring a worker that then fails is
 *   worse than none: it disables that fallback.)
 * - **Explicit control**: pass `workerPort` (a pre-instantiated worker) or
 *   `workerSrc` (a URL); either takes precedence and is left untouched.
 *
 * Which pdf.js BUILD answers to `pdfjs-dist` is deliberately not decided here.
 * The bare specifier is the contract: a published plugin resolves it through
 * an importmap that maps package names only, so a `pdfjs-dist/legacy/...`
 * subpath would be a link-time failure for the whole plugin. Each host picks
 * the build: the web client aliases `pdfjs-dist` to `legacy/build` (its
 * `config/pdfjs-legacy-alias.ts` says why — the modern build needs Safari
 * 26.2+ and blanked every page on iOS 18), sandboxed plugins get whatever the
 * vendored-library registry serves from the CDN, and the server uses the
 * legacy build directly in `pdf-rasterize.ts`.
 */

type PdfJsModule = typeof import('pdfjs-dist');

export interface LoadPdfJsOptions {
  /** A pre-instantiated worker. Takes highest precedence. */
  workerPort?: Worker;
  /** An explicit worker script URL/string. Used when `workerPort` is absent. */
  workerSrc?: string;
  /**
   * A pdf.js worker MODULE url (e.g. the platform-hosted
   * `https://cdn.<env>.fias.io/pdfjs/<ver>/pdf.worker.min.mjs`). The loader
   * wraps it in a `blob:` module worker — the sandboxed-plugin path. Ignored
   * when `workerPort`/`workerSrc` is set. Failures fall back to main-thread.
   */
  workerModuleUrl?: string;
}

/** pdf.js getDocument params we default centrally. `cMapUrl`/`standardFontDataUrl`
 *  are the origins from which the worker fetches CMap + standard-font data for
 *  CID / non-embedded fonts (anything beyond basic embedded-font PDFs); they
 *  require a `connect-src` allowance for that origin. */
interface PdfAssetConfig {
  cMapUrl?: string;
  standardFontDataUrl?: string;
}

let cached: Promise<PdfJsModule> | null = null;
let assetConfig: PdfAssetConfig = {};

/**
 * Configure where the pdf.js worker fetches CMaps + standard fonts. Call once
 * before the first `getPdfDocumentParams`/render. In a plugin this is the
 * platform CDN base (`https://cdn.<env>.fias.io/pdfjs/<ver>/cmaps/` etc.); the
 * `sandbox:vendored-libraries` capability must allow that origin in `connect-src`.
 */
export function configurePdfAssets(config: PdfAssetConfig): void {
  assetConfig = { ...config };
}

/**
 * Build a `blob:` module worker that imports a pdf.js worker module from a
 * (CSP-allowed) URL. This is the sandboxed-plugin delivery: creating the worker
 * needs `worker-src blob:`; the `import` inside needs the module URL's origin in
 * `worker-src` too (a module worker's import graph is worker-src-governed).
 *
 * Exported for callers that manage `GlobalWorkerOptions.workerPort` directly.
 */
export function createBlobModuleWorker(workerModuleUrl: string): Worker {
  // JSON.stringify safely quotes the URL into a JS string literal.
  const body = `import ${JSON.stringify(workerModuleUrl)};`;
  const blobUrl = URL.createObjectURL(new Blob([body], { type: 'text/javascript' }));
  return new Worker(blobUrl, { type: 'module' });
}

export function loadPdfJs(options?: LoadPdfJsOptions): Promise<PdfJsModule> {
  if (!cached) {
    cached = (async () => {
      const pdfjs = await import('pdfjs-dist');
      const opts = pdfjs.GlobalWorkerOptions;
      if (options?.workerPort) {
        opts.workerPort = options.workerPort;
      } else if (options?.workerSrc) {
        opts.workerSrc = options.workerSrc;
      } else if (options?.workerModuleUrl && !opts.workerPort && !opts.workerSrc) {
        // Sandboxed-plugin path: wrap the CDN worker module in a blob: module
        // worker. If the environment refuses to create it (CSP, no Worker),
        // leave the options unset so pdf.js uses its main-thread fake worker
        // rather than a broken port (which would disable that fallback).
        try {
          opts.workerPort = createBlobModuleWorker(options.workerModuleUrl);
        } catch {
          // fall through to main-thread fake worker
        }
      } else if (!opts.workerPort && !opts.workerSrc) {
        // No worker configured by the caller — default to the bundler-
        // fingerprinted URL. Works on a host page where the worker can be
        // fetched; a sandboxed plugin must instead pass workerModuleUrl/
        // workerPort/workerSrc, which the guards above preserve.
        opts.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
      }
      return pdfjs;
    })();
  }
  return cached;
}

/**
 * Shared getDocument parameters: the document bytes plus platform defaults.
 * `isEvalSupported: false` because the plugin/preview CSP has no
 * `'unsafe-eval'` — pdf.js must not try to `eval` its font renderer. CMap +
 * standard-font URLs are added only when configured via `configurePdfAssets`.
 */
export function getPdfDocumentParams(
  bytes: Uint8Array,
  overrides?: Record<string, unknown>,
): Record<string, unknown> {
  const params: Record<string, unknown> = {
    data: bytes.slice(),
    isEvalSupported: false,
  };
  if (assetConfig.cMapUrl) {
    params.cMapUrl = assetConfig.cMapUrl;
    params.cMapPacked = true;
  }
  if (assetConfig.standardFontDataUrl) {
    params.standardFontDataUrl = assetConfig.standardFontDataUrl;
  }
  return { ...params, ...overrides };
}

/** Page count via pdf.js, which is far more lenient than pdf-lib and renders
 * many PDFs pdf-lib can't parse. Used for the viewer so a document can still
 * be viewed even when pdf-lib editing isn't possible. */
export async function getPdfJsPageCount(bytes: Uint8Array): Promise<number> {
  const pdfjs = await loadPdfJs();
  const doc = await pdfjs.getDocument(getPdfDocumentParams(bytes)).promise;
  try {
    return doc.numPages;
  } finally {
    // pdf.js 6 removed `PDFDocumentProxy.destroy()`, which was only ever an
    // alias for this. Tearing down the loading task is what actually aborts
    // in-flight requests and terminates the worker.
    await doc.loadingTask.destroy();
  }
}
